import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Titre masqué visuellement mais lu par les lecteurs d'écran. */
  hideTitle?: boolean;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onOpenAutoFocus?: (event: Event) => void;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  hideTitle,
  children,
  className,
  style,
  onOpenAutoFocus,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/50 dark:bg-black/60" />
        <RadixDialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={onOpenAutoFocus}
          style={style}
          className={cn(
            'animate-pop-in fixed top-1/2 left-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'flex flex-col overflow-hidden rounded-lg border border-line bg-bg text-fg shadow-dialog focus:outline-none',
            className,
          )}
        >
          <RadixDialog.Title
            className={hideTitle ? 'sr-only' : 'px-6 pt-5 pb-2 text-base font-medium'}
          >
            {title}
          </RadixDialog.Title>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export const DialogClose = RadixDialog.Close;

/** Boîte de confirmation simple (suppression définitive, etc.). */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} className="max-w-md">
      <p className="px-6 pb-4 text-sm text-muted">{message}</p>
      <div className="flex justify-end gap-2 px-4 pb-4">
        <DialogClose asChild>
          <button type="button" className="h-9 rounded px-6 text-sm font-medium hover:bg-hover">
            Annuler
          </button>
        </DialogClose>
        <button
          type="button"
          className="h-9 rounded px-6 text-sm font-medium text-primary hover:bg-hover"
          onClick={() => {
            onConfirm();
            onOpenChange(false);
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
